// BKG performance harness - not a product test. See __perf__/README.md.
// Counts what one page of BKG company results actually mounts.

import React from "react";
import { render, act } from "@testing-library/react";
import { SearchResults } from "../SearchResults";

jest.setTimeout(900000);

type ObsRecord = { target: Node; options: MutationObserverInit };
let observerCount = 0;
let observeCalls: ObsRecord[] = [];
let callbackInvocations = 0;

const RealMO = global.MutationObserver;

class CountingMutationObserver {
  private inner: MutationObserver;
  constructor(cb: MutationCallback) {
    observerCount += 1;
    this.inner = new RealMO((...args: any[]) => {
      callbackInvocations += 1;
      return (cb as any)(...args);
    });
  }
  observe(target: Node, options?: MutationObserverInit) {
    observeCalls.push({ target, options: options ?? {} });
    return this.inner.observe(target, options);
  }
  disconnect() {
    return this.inner.disconnect();
  }
  takeRecords() {
    return this.inner.takeRecords();
  }
}

const reset = () => {
  observerCount = 0;
  observeCalls = [];
  callbackInvocations = 0;
};

/**
 * Layout is now a JS decision (useWideLayout), and setupTests stubs matchMedia
 * to answer false for everything -- which would silently measure the mobile card
 * list. Report a real width so the numbers below describe the desktop table, the
 * case the 50-row page actually hits.
 */
const setViewportWidth = (width: number) => {
  (window as any).matchMedia = (query: string) => {
    const min = /min-width:\s*(\d+(?:\.\d+)?)px/.exec(query);
    const max = /max-width:\s*(\d+(?:\.\d+)?)px/.exec(query);
    let matches = true;
    if (min) matches = matches && width >= parseFloat(min[1]);
    if (max) matches = matches && width <= parseFloat(max[1]);
    if (!min && !max) matches = false;
    return {
      matches,
      media: query,
      onchange: null,
      addListener() {},
      removeListener() {},
      addEventListener() {},
      removeEventListener() {},
      dispatchEvent() {
        return true;
      },
    };
  };
};

const DESKTOP_WIDTH = 1440;
const MOBILE_WIDTH = 600;

beforeAll(() => {
  (global as any).MutationObserver = CountingMutationObserver;
  if (!(global as any).ResizeObserver) {
    (global as any).ResizeObserver = class {
      observe() {}
      unobserve() {}
      disconnect() {}
    };
  }
});

beforeEach(() => setViewportWidth(DESKTOP_WIDTH));

afterAll(() => {
  (global as any).MutationObserver = RealMO;
});

const autodb = (value: any) => ({
  value,
  source_id: "https://example.test/source",
  index_date: "2026-01-04T00:00:00Z",
  source_date: "2025-11-30T00:00:00Z",
  data_comment: "Extracted from the latest filing.",
});

const makeCompany = (i: number) => ({
  _id: `ws-1/company/c-${i}`,
  value: {
    name: autodb(`Company ${i}`),
    headquarters: autodb({
      city: autodb("Boston"),
      state: autodb("MA"),
      country: autodb("USA"),
      continent: autodb("North America"),
    }),
    lead_pipeline_asset: autodb({
      therapeutic_area: autodb([
        { value: "Oncology" },
        { value: "Immunology" },
      ]),
      development_stage: autodb("Phase II"),
    }),
    number_of_employees: autodb(120 + i),
    latest_funding: autodb({
      amount: autodb(45000000),
      type: autodb("Series B"),
    }),
    hubspot_owner: autodb("A. Owner"),
    attendance: [],
  },
});

const renderResults = (n: number) =>
  render(
    <SearchResults
      loading={false}
      results={Array.from({ length: n }, (_, i) => makeCompany(i))}
      onCompanyClick={() => {}}
      hrefForCompany={(id) => `/c/${id}`}
    />,
  );

const report: string[] = [];
const log = (line: string) => report.push(line);

afterAll(() => {
  // eslint-disable-next-line no-console
  console.log(
    "\n\n===== STEP 0: BKG RESULTS PAGE =====\n" + report.join("\n") + "\n",
  );
});

describe("results page structural cost", () => {
  it("scales tooltip instances and observers with row count", () => {
    for (const n of [1, 5, 25]) {
      reset();
      const t0 = Date.now();
      const { container, unmount } = renderResults(n);
      const mountMs = Date.now() - t0;

      const anchors = container.querySelectorAll("[data-tooltip-id]").length;
      const bodyWide = observeCalls.filter(
        (o) => o.target === document.body && o.options.subtree,
      ).length;
      const rows = container.querySelectorAll("tbody tr").length;
      const cards = container.querySelectorAll('[class*="MuiCard-root"]').length;
      const nodes = container.querySelectorAll("*").length;

      log(
        `rows=${String(n).padStart(2)} | tooltip anchors=${String(anchors).padStart(3)}` +
          ` | MutationObservers=${String(observerCount).padStart(3)}` +
          ` (document.body+subtree: ${bodyWide})` +
          ` | table rows=${rows} cards=${cards}` +
          ` | DOM nodes=${String(nodes).padStart(5)}` +
          ` | jsdom mount=${mountMs}ms (${(mountMs / n).toFixed(0)}ms/row)`,
      );
      unmount();
    }
  });

  it("mounts one layout, not both", () => {
    for (const [label, width] of [
      ["desktop", DESKTOP_WIDTH],
      ["mobile", MOBILE_WIDTH],
    ] as Array<[string, number]>) {
      setViewportWidth(width);
      reset();
      const { container, unmount } = renderResults(25);
      const rows = container.querySelectorAll("tbody tr").length;
      const cards = container.querySelectorAll('[class*="MuiCard-root"]').length;
      const nodes = container.querySelectorAll("*").length;
      log(
        `${label.padEnd(7)} (${width}px) | table rows=${String(rows).padStart(2)} | ` +
          `cards=${String(cards).padStart(2)} | DOM nodes=${String(nodes).padStart(5)}`,
      );
      unmount();
    }
    setViewportWidth(DESKTOP_WIDTH);
  });

  it("measures observer fan-out from a single unrelated DOM mutation", async () => {
    reset();
    const { unmount } = renderResults(25);
    const live = observerCount;

    callbackInvocations = 0;
    const probe = document.createElement("div");
    await act(async () => {
      document.body.appendChild(probe);
      await new Promise((r) => setTimeout(r, 0));
    });

    log(
      `\nOne appendChild to document.body, 25 rows mounted:` +
        `\n  live MutationObservers = ${live}` +
        `\n  observer callbacks fired by that ONE mutation = ${callbackInvocations}`,
    );
    probe.remove();
    unmount();
  });
});
